<template>
  <div class="soar-container flex flex-col gap-y-4">
    <h1 class="pb-2 flex justify-between items-end border-b border-brand-grayblue-300">
      <span class="text-3xl">{{ $t('familymembers') }}</span>
      <a href="#" class="pb-0.5 text-primary">{{ $t('guardianshipFamilyMembers') }}</a>
    </h1>
    <InfoBar :text="$t('citizenFamilyManageNote')"></InfoBar>
    <SearchForm @on-search="handleSearch"></SearchForm>
    <FeesTableSkeleton v-if="isLoading"></FeesTableSkeleton>
    <template v-else>
      <DataTable
        v-if="dataSet"
        :default-headers="[
          'unifiedNumber',
          'name',
          'relationship',
          'nationality',
          'identityNumber',
          'eidaCardExpiryDate',
          'actions'
        ]"
        :data-set="dataSet"
      >
        <template #headerCell="{ label }">
          <span v-if="label == 'actions'"></span>
        </template>
        <template #cell="{ columnName, columnValue, rowIndex }">
          <span v-if="columnName == 'name'" v-html="columnValue"></span>
          <div v-if="columnName == 'actions'" class="w-full h-full flex gap-1">
            <button class="rounded p-2 bg-cyan-400">
              <faEye class="w-3 fill-white" />
            </button>
            <button v-if="false" class="rounded p-2 bg-gray-600">
              <faPrint class="w-3 fill-white" />
            </button>
            <tippy
              v-if="hasServices(rowIndex)"
              :theme="useThemeStore().currentScheme == 'light' ? 'light-border' : 'dark-border'"
              :arrow="false"
              :offset="[0, 4]"
              interactive
              trigger="click"
              v-bind="{ ...(maxDate && { maxDate: new Date() }) }"
              placement="bottom-end"
            >
              <button class="rounded p-2 bg-gray-600" @click.prevent="">
                <faBars class="w-3 fill-white" />
              </button>
              <template #content>
                <div class="flex flex-col gap-y-2 max-h-[12rem] overflow-y-auto">
                  <button
                    :key="key"
                    v-for="(item, key) in findAllowedServices(rowIndex)"
                    class="text-start px-3 py-1.5 text-sm text-brand-black-secondary hover:text-primary-700 hover:bg-gray-200"
                    @click="onServiceClick(item, rowIndex)"
                  >
                    {{ item.transactionType.text }}
                  </button>
                </div>
              </template>
            </tippy>
          </div>
        </template>
      </DataTable>
    </template>
  </div>
</template>

<script setup>
import faBars from '@/assets/icons/faBars.svg?component'
import faEye from '@/assets/icons/faEye.svg?component'
import faPrint from '@/assets/icons/faPrint.svg?component'
import clientLookups from '@/assets/json/clientLookups.json'
import DataTable from '@/components/DataTable/index.vue'
import InfoBar from '@/components/Form/InfoBar.vue'
import FeesTableSkeleton from '@/components/skeletons/FeesTableSkeleton.vue'
import i18n from '@/i18n/i18n'
import axios from '@/services'
import { useAuthStore } from '@/stores/auth'
import { useFamilyBookStore } from '@/stores/family_book'
import { useLookupsStore } from '@/stores/lookups'
import { useThemeStore } from '@/stores/theme'
import SearchForm from '@/views/individual/family-members/SearchForm.vue'
import { notify } from 'notiwind'
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'

const route = useRoute()
const router = useRouter()

const familyBookStore = useFamilyBookStore()
const authStore = useAuthStore()
const lookupsStore = useLookupsStore()
const isLoading = ref(true)
const moduleId = ref()
const transactions = ref()

onMounted(async () => {
  transactions.value = await lookupsStore.fetchLookup('/api/lookup/transactions')
  await authStore.fetchIndividualInfo()

  const payload = {}

  if ('transactionId' in route.query) {
    payload.serviceTransactionId = route.query.transactionId
  }

  await getFamilyMembers(payload)
})

const dataSet = computed(() => {
  if ((familyBookStore.members?.collection || []).length === 0) return

  return familyBookStore.members.collection.map((f) => {
    return {
      unifiedNumber: f.unifiedNumber,
      name: `<div class="flex flex-col gap-y-2"><span>${f.arabicName}</span><span>${f.englishName}</span></div>`,
      relationship: f.familyRelationship.text,
      nationality: f.nationality.text,
      identityNumber: f.identityNumber,
      eidaCardExpiryDate: f.eidaCardExpireDate,
      actions: ''
    }
  })
})

const handleSearch = async (searchCriteria) => {
  await getFamilyMembers(searchCriteria)
}

async function getFamilyMembers(searchCriteria = {}) {
  isLoading.value = true
  // familyBookStore.members = await axios.post(
  //   '/api/individual/citizen/getFamilyBookMembers',
  //   searchCriteria
  // )

  await familyBookStore.getFamilyBookMembers(searchCriteria)

  isLoading.value = false
}

const hasServices = (rowIndex) => {
  const { allowedServiceTransactions } = familyBookStore.members?.collection?.[rowIndex] || {}
  return (allowedServiceTransactions || []).length > 0
}

const findAllowedServices = (rowIndex) => {
  const { allowedServiceTransactions } = familyBookStore.members.collection[rowIndex]
  return allowedServiceTransactions
}

const onServiceClick = async (service, familyMemberIndex) => {
  const familyMember = familyBookStore.members.collection[familyMemberIndex]

  const { actionId, serviceTransactionId } = service

  const {
    ServiceTransactions: { issuePassport, replacePassport, issuePassportFromOutsideUae },
    module: { ICA, familyBook },
    eidaServices: { renewIDForCitizen }
  } = clientLookups

  moduleId.value = transactions.value.find((tx) => tx.id === actionId)?.moduleId

  if (moduleId.value === ICA) {
    return await handleIcaAction(service, familyMember)
  }

  if (moduleId.value === familyBook) {
    return await handleFamilyBookAction(service, familyMember)
  }

  const { defaultImmigrationDepartmentId } = authStore.individualInfo

  // await globalStore.fetchConfig(serviceTransactionId)
  router.push({
    name: 'ServiceForm',
    params: {
      category: 'citizen',
      serviceId: serviceTransactionId
    }
  })
}

const handleIcaAction = async (service, familyMember) => {
  const { serviceTransactionId } = service
  const {
    eidaServices: { renewIDForCitizen }
  } = clientLookups

  const isNewUaeCitizen = await axios.get('/api/icaRequest/isNewUaeCitizen', {
    params: {
      personUnifiedNumber: familyMember.personUnifiedNumber,
      personIdentityNumber: familyMember.identityNumber
    }
  })

  if (serviceTransactionId === renewIDForCitizen) {
    if (!familyMember.eidaCardExpireDate) {
      notify(
        {
          group: 'error',
          title: i18n.t('errorSetDefaultAddress'),
          text: i18n.t('cantApplyWarningMessage')
        },
        6000
      )
    }

    if (
      familyMember.eidaCardExpireDate &&
      !isNewUaeCitizen &&
      isEidExpired(familyMember.eidaCardExpireDate)
    ) {
      notify(
        {
          group: 'error',
          title: i18n.t('errorSetDefaultAddress'),
          text: i18n.t('eidaCardValidationMsg')
        },
        6000
      )
    }
  }

  // TODO: continue this
}

const handleFamilyBookAction = async (service, familyMember) => {
  const { serviceTransactionId } = service
  router.push({
    name: 'ServiceForm',
    params: {
      serviceId: serviceTransactionId
    },
    query: {
      personUN: familyMember.unifiedNumber,
      familyRelationship: familyMember.familyRelationship.id,
      wifeCityCode: familyMember.cityCode,
      wifeFamilySequency: familyMember.familySequence
    }
  })
}

/**
 * checks `date` if it's expired
 */
const isEidExpired = (date) => {
  return false
}
</script>

<style lang="scss" scoped></style>
